if (!customElements.get('featured-collection')) {
  customElements.define(
    'featured-collection',
    class FeaturedCollection extends HTMLElement {
      constructor() {
        super();

        this.mobileAutoScroll = null;
        this.desktopAutoScroll = null;
        this.resumeAutoScrollTimeout = null;
        this.sliderInstance = false;

        this.mobileTouchStartHandler = null;
        this.mobileTouchEndHandler = null;
        this.mobileMouseDownHandler = null;
        this.mobileMouseUpHandler = null;
      }

      connectedCallback() {
        this.selectors = {
          sliderWrapper: '.featured-collection__items',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          pagination: '.swiper-pagination',
        };

        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.blockId = this.dataset.blockId;

        this.block = this.closest(
          `[data-id="${this.blockId}"]`
        );

        this.sliderWrapper = this.querySelector(
          this.selectors.sliderWrapper
        );

        this.enableSlider =
          this.dataset.enableSlider === 'true';

        this.items = parseInt(
          this.dataset.items || 4,
          10
        );

        this.tabletItems = parseInt(
          this.dataset.tabletItems || 3,
          10
        );

        this.laptopItems = parseInt(
          this.dataset.laptopItems || 4,
          10
        );

        this.paginationType =
          this.dataset.paginationType || 'bullets';

        if (!this.sliderWrapper) return;

        const mql = window.matchMedia(
          FoxTheme.config.mediaQueryMobile
        );

        this.mql = mql;

        if (mql.addEventListener) {
          mql.addEventListener(
            'change',
            () => {
              this.init();
            }
          );
        } else {
          mql.onchange = () => {
            this.init();
          };
        }

        this.init();
      }

      /*
       * INITIALIZE
       */
      init() {
        if (!this.sliderWrapper) return;

        const isMobile =
          window.matchMedia(
            FoxTheme.config.mediaQueryMobile
          ).matches;

        if (isMobile) {
          /*
           * MOBILE
           */
          this.destroySlider();
          this.setupMobileScrolling();
        } else {
          /*
           * DESKTOP
           */
          this.stopMobileAutoScroll();

          if (this.enableSlider) {
            this.initSlider();
          }
        }
      }

      /*
       * DESKTOP SLIDER
       */
      initSlider() {
        if (!this.enableSlider) return;

        if (
          this.sliderInstance &&
          typeof this.sliderInstance === 'object'
        ) {
          return;
        }

        const columnGap =
          window
            .getComputedStyle(this.sliderWrapper)
            .getPropertyValue('--f-column-gap');

        const spaceBetween =
          parseFloat(
            columnGap.replace('rem', '')
          ) * 10 || 0;

        this.sliderOptions = {
          slidesPerView: 2,

          spaceBetween: spaceBetween,

          navigation: {
            nextEl: this.block
              ? this.block.querySelector(
                  this.selectors.nextEl
                )
              : null,

            prevEl: this.block
              ? this.block.querySelector(
                  this.selectors.prevEl
                )
              : null,
          },

          pagination: {
            el: this.block
              ? this.block.querySelector(
                  this.selectors.pagination
                )
              : null,

            type: this.paginationType,
          },

          breakpoints: {
            768: {
              slidesPerView: this.tabletItems,
            },

            1024: {
              slidesPerView: this.laptopItems,
            },

            1280: {
              slidesPerView: this.items,
            },
          },

          loop: false,

          threshold: 2,

          watchSlidesProgress: true,

          mousewheel: {
            enabled: true,
            forceToAxis: true,
          },

          /*
           * Allow manual dragging.
           */
          allowTouchMove: true,
        };

        this.classList.add(
          this.classes.swiper
        );

        this.sliderWrapper.classList.remove(
          this.classes.grid
        );

        this.sliderWrapper.classList.add(
          this.classes.swiperWrapper
        );

        this.sliderInstance =
          new window.FoxTheme.Carousel(
            this,
            this.sliderOptions,
            [FoxTheme.Swiper.Mousewheel]
          );

        this.sliderInstance.init();

        /*
         * START DESKTOP AUTO-SCROLL
         */
        if (
          this.sliderInstance &&
          this.sliderInstance.slider
        ) {
          const swiper =
            this.sliderInstance.slider;

          swiper.allowTouchMove = true;

          /*
           * Start automatically.
           */
          this.startDesktopAutoScroll();

          /*
           * Pause when user touches/drags.
           */
          swiper.on('touchStart', () => {
            this.stopDesktopAutoScroll();
          });

          /*
           * Resume after user releases.
           */
          swiper.on('touchEnd', () => {
            this.resumeDesktopAutoScroll();
          });
        }

        this.fixQuickviewDuplicate();

        this.calcNavButtonsPosition();
      }

      /*
       * DESKTOP AUTO-SCROLL
       *
       * Moves one slide every 3 seconds.
       */
      startDesktopAutoScroll() {
        this.stopDesktopAutoScroll();

        if (
          !this.sliderInstance ||
          !this.sliderInstance.slider
        ) {
          return;
        }

        this.desktopAutoScroll =
          setInterval(() => {
            if (
              !this.sliderInstance ||
              !this.sliderInstance.slider
            ) {
              return;
            }

            const swiper =
              this.sliderInstance.slider;

            if (
              swiper.destroyed
            ) {
              return;
            }

            /*
             * Go back to first slide
             * when reaching the end.
             */
            if (swiper.isEnd) {
              swiper.slideTo(0);
            } else {
              swiper.slideNext();
            }
          }, 3000);
      }

      /*
       * STOP DESKTOP AUTO-SCROLL
       */
      stopDesktopAutoScroll() {
        if (this.desktopAutoScroll) {
          clearInterval(
            this.desktopAutoScroll
          );

          this.desktopAutoScroll = null;
        }
      }

      /*
       * RESUME DESKTOP AUTO-SCROLL
       */
      resumeDesktopAutoScroll() {
        if (this.resumeAutoScrollTimeout) {
          clearTimeout(
            this.resumeAutoScrollTimeout
          );
        }

        this.resumeAutoScrollTimeout =
          setTimeout(() => {
            this.startDesktopAutoScroll();
          }, 3000);
      }

      /*
       * MOBILE SCROLLING SETUP
       */
      setupMobileScrolling() {
        this.stopMobileAutoScroll();

        if (!this.sliderWrapper) return;

        const slides =
          this.sliderWrapper.querySelectorAll(
            '.f-column'
          );

        if (slides.length <= 1) {
          return;
        }

        /*
         * IMPORTANT:
         * Allow horizontal finger swiping.
         */
        this.sliderWrapper.style.setProperty(
          'touch-action',
          'pan-x pan-y',
          'important'
        );

        this.sliderWrapper.style.setProperty(
          'overflow-x',
          'auto',
          'important'
        );

        this.sliderWrapper.style.setProperty(
          'overscroll-behavior-x',
          'auto',
          'important'
        );

        this.sliderWrapper.style.scrollBehavior =
          'smooth';

        /*
         * Create touch handlers only once.
         */
        if (
          !this.mobileTouchStartHandler
        ) {
          this.mobileTouchStartHandler =
            () => {
              this.stopMobileAutoScroll();
            };
        }

        if (
          !this.mobileTouchEndHandler
        ) {
          this.mobileTouchEndHandler =
            () => {
              this.resumeMobileAutoScroll();
            };
        }

        /*
         * Touch controls.
         */
        this.sliderWrapper.removeEventListener(
          'touchstart',
          this.mobileTouchStartHandler
        );

        this.sliderWrapper.removeEventListener(
          'touchend',
          this.mobileTouchEndHandler
        );

        this.sliderWrapper.addEventListener(
          'touchstart',
          this.mobileTouchStartHandler,
          { passive: true }
        );

        this.sliderWrapper.addEventListener(
          'touchend',
          this.mobileTouchEndHandler,
          { passive: true }
        );

        /*
         * Mouse controls.
         */
        if (
          !this.mobileMouseDownHandler
        ) {
          this.mobileMouseDownHandler =
            () => {
              this.stopMobileAutoScroll();
            };
        }

        if (
          !this.mobileMouseUpHandler
        ) {
          this.mobileMouseUpHandler =
            () => {
              this.resumeMobileAutoScroll();
            };
        }

        this.sliderWrapper.removeEventListener(
          'mousedown',
          this.mobileMouseDownHandler
        );

        this.sliderWrapper.removeEventListener(
          'mouseup',
          this.mobileMouseUpHandler
        );

        this.sliderWrapper.addEventListener(
          'mousedown',
          this.mobileMouseDownHandler
        );

        this.sliderWrapper.addEventListener(
          'mouseup',
          this.mobileMouseUpHandler
        );

        /*
         * Start automatically.
         *
         * Small delay gives the theme time
         * to finish rendering the products.
         */
        setTimeout(() => {
          if (
            this.sliderWrapper &&
            document.body.contains(this)
          ) {
            this.startMobileAutoScroll();
          }
        }, 1000);
      }

      /*
       * MOBILE AUTO-SCROLL
       *
       * Moves one product every 3 seconds.
       */
      startMobileAutoScroll() {
        this.stopMobileAutoScroll(false);

        if (!this.sliderWrapper) {
          return;
        }

        const slides =
          this.sliderWrapper.querySelectorAll(
            '.f-column'
          );

        if (slides.length <= 1) {
          return;
        }

        this.mobileAutoScroll =
          setInterval(() => {
            if (!this.sliderWrapper) {
              return;
            }

            const currentSlides =
              this.sliderWrapper.querySelectorAll(
                '.f-column'
              );

            if (currentSlides.length <= 1) {
              return;
            }

            const firstSlide =
              currentSlides[0];

            if (!firstSlide) {
              return;
            }

            const slideRect =
              firstSlide.getBoundingClientRect();

            const slideWidth =
              slideRect.width;

            if (slideWidth <= 0) {
              return;
            }

            /*
             * Find the gap between products.
             */
            const styles =
              window.getComputedStyle(
                this.sliderWrapper
              );

            let gap =
              parseFloat(
                styles.columnGap
              ) || 0;

            if (!gap) {
              gap =
                parseFloat(
                  styles.gap
                ) || 0;
            }

            /*
             * Fallback for themes that use
             * margins instead of gap.
             */
            if (
              !gap &&
              currentSlides.length > 1
            ) {
              const secondSlide =
                currentSlides[1];

              const firstRect =
                firstSlide.getBoundingClientRect();

              const secondRect =
                secondSlide.getBoundingClientRect();

              gap =
                secondRect.left -
                firstRect.right;

              if (gap < 0) {
                gap = 0;
              }
            }

            const scrollAmount =
              slideWidth + gap;

            const maxScroll =
              this.sliderWrapper.scrollWidth -
              this.sliderWrapper.clientWidth;

            /*
             * There isn't enough content to scroll.
             */
            if (maxScroll <= 2) {
              return;
            }

            /*
             * If we're at the end,
             * smoothly return to the beginning.
             */
            if (
              this.sliderWrapper.scrollLeft >=
              maxScroll - 5
            ) {
              this.sliderWrapper.scrollTo({
                left: 0,
                behavior: 'smooth',
              });

              return;
            }

            /*
             * Move exactly one product.
             */
            this.sliderWrapper.scrollBy({
              left: scrollAmount,
              behavior: 'smooth',
            });
          }, 3000);
      }

      /*
       * STOP MOBILE AUTO-SCROLL
       */
      stopMobileAutoScroll(
        clearResume = true
      ) {
        if (this.mobileAutoScroll) {
          clearInterval(
            this.mobileAutoScroll
          );

          this.mobileAutoScroll = null;
        }

        if (
          clearResume &&
          this.resumeAutoScrollTimeout
        ) {
          clearTimeout(
            this.resumeAutoScrollTimeout
          );

          this.resumeAutoScrollTimeout =
            null;
        }
      }

      /*
       * RESUME MOBILE AUTO-SCROLL
       */
      resumeMobileAutoScroll() {
        if (this.resumeAutoScrollTimeout) {
          clearTimeout(
            this.resumeAutoScrollTimeout
          );
        }

        this.resumeAutoScrollTimeout =
          setTimeout(() => {
            this.startMobileAutoScroll();
          }, 3000);
      }

      /*
       * DESTROY DESKTOP SLIDER
       * WHEN SWITCHING TO MOBILE.
       */
      destroySlider() {
        this.stopDesktopAutoScroll();

        this.classList.remove(
          this.classes.swiper
        );

        if (this.sliderWrapper) {
          this.sliderWrapper.classList.remove(
            this.classes.swiperWrapper
          );

          this.sliderWrapper.classList.add(
            this.classes.grid
          );
        }

        if (
          !this.sliderInstance ||
          typeof this.sliderInstance !==
            'object'
        ) {
          return;
        }

        if (
          this.sliderInstance.slider &&
          !this.sliderInstance.slider.destroyed
        ) {
          this.sliderInstance.slider.destroy();
        }

        this.sliderInstance = false;
      }

      /*
       * QUICK VIEW
       */
      fixQuickviewDuplicate() {
        const modalIds = [];

        Array.from(
          this.querySelectorAll(
            'quick-view-modal'
          )
        ).forEach((modal) => {
          const modalID =
            modal.getAttribute('id');

          if (
            modalIds.includes(modalID)
          ) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }

      /*
       * NAVIGATION POSITION
       */
      calcNavButtonsPosition() {
        if (
          this.dataset.navigationPosition !==
          'center_image'
        ) {
          return;
        }

        const firstMedia =
          this.querySelector(
            '.product-card__image-wrapper'
          );

        const controls =
          this.block
            ? this.block.querySelector(
                '.swiper-controls'
              )
            : null;

        if (
          controls &&
          firstMedia &&
          firstMedia.clientHeight > 0
        ) {
          controls.style.setProperty(
            '--swiper-navigation-top-offset',
            parseInt(
              firstMedia.clientHeight
            ) / 2 +
              'px'
          );
        }
      }

      /*
       * CLEAN UP
       */
      disconnectedCallback() {
        this.stopMobileAutoScroll();
        this.stopDesktopAutoScroll();

        if (
          this.resumeAutoScrollTimeout
        ) {
          clearTimeout(
            this.resumeAutoScrollTimeout
          );

          this.resumeAutoScrollTimeout =
            null;
        }

        if (
          this.sliderWrapper
        ) {
          if (
            this.mobileTouchStartHandler
          ) {
            this.sliderWrapper.removeEventListener(
              'touchstart',
              this.mobileTouchStartHandler
            );
          }

          if (
            this.mobileTouchEndHandler
          ) {
            this.sliderWrapper.removeEventListener(
              'touchend',
              this.mobileTouchEndHandler
            );
          }

          if (
            this.mobileMouseDownHandler
          ) {
            this.sliderWrapper.removeEventListener(
              'mousedown',
              this.mobileMouseDownHandler
            );
          }

          if (
            this.mobileMouseUpHandler
          ) {
            this.sliderWrapper.removeEventListener(
              'mouseup',
              this.mobileMouseUpHandler
            );
          }
        }
      }
    }
  );
}